import { useState, useRef } from "react";
import { Eye, EyeOff } from "lucide-react";

/**
 * Press-and-hold password reveal input.
 *
 * Behaviour:
 * - Press (mousedown / touchstart / keyboard hold on the eye) => value is shown as plain text.
 * - Release (mouseup / touchend / blur / mouseleave / keyup) => value is masked back immediately.
 *
 * The password is NEVER stuck in a "visible" state — this protects the user in case they
 * walk away from their desk mid-input.
 */
export default function PasswordInput({
  value,
  onChange,
  className = "br-input",
  autoComplete = "current-password",
  required = false,
  autoFocus = false,
  "data-testid": dataTestId,
  ariaLabel,
  ...rest
}) {
  const [visible, setVisible] = useState(false);
  const inputRef = useRef(null);

  const reveal = () => setVisible(true);
  const hide = () => setVisible(false);

  return (
    <div className="relative">
      <input
        ref={inputRef}
        type={visible ? "text" : "password"}
        className={`${className} pr-12`}
        value={value}
        onChange={onChange}
        required={required}
        autoFocus={autoFocus}
        autoComplete={autoComplete}
        data-testid={dataTestId}
        aria-label={ariaLabel}
        {...rest}
      />
      <button
        type="button"
        tabIndex={-1}
        aria-label={visible ? "Hide password" : "Show password (press and hold)"}
        onMouseDown={(e) => {
          e.preventDefault();
          reveal();
        }}
        onMouseUp={hide}
        onMouseLeave={hide}
        onTouchStart={(e) => {
          e.preventDefault();
          reveal();
        }}
        onTouchEnd={hide}
        onTouchCancel={hide}
        onBlur={hide}
        onKeyDown={(e) => {
          if (e.key === " " || e.key === "Enter") {
            e.preventDefault();
            reveal();
          }
        }}
        onKeyUp={hide}
        className="absolute right-2 top-1/2 -translate-y-1/2 p-2 text-[color:var(--br-text-dim)] hover:text-[color:var(--br-bronze-soft)] transition-colors focus:outline-none focus:text-[color:var(--br-bronze-soft)]"
        data-testid={dataTestId ? `${dataTestId}-toggle` : "password-toggle"}
        style={{ touchAction: "none" }}
      >
        {visible ? <EyeOff size={18} /> : <Eye size={18} />}
      </button>
    </div>
  );
}
